import { useMemo, useState } from 'react';

import { api } from '../api';
import type { PortalConfig } from '../config';
import { SEVERITIES, buildFixPrompt, downloadCsv, downloadText, plainText } from '../reports';
import type { CodeFinding, CodeSnippet, ScanCategory } from '../types';
import { AsyncBoundary, Notice, SeverityBadge, formatDate, useAsync } from '../ui';

const CATEGORIES: ScanCategory[] = ['SAST', 'SCA', 'IAC'];

const CATEGORY_LABEL: Record<ScanCategory, string> = {
  SAST: 'SAST (code)',
  SCA: 'SCA (dependencies)',
  IAC: 'IaC (infrastructure)',
};

export function CodeFindingsView({ config, isAdmin }: { config: PortalConfig; isAdmin: boolean }) {
  const [severity, setSeverity] = useState('');
  const [category, setCategory] = useState('');
  const [repository, setRepository] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ACTIVE');

  // Severity and status are filtered server-side because they narrow the result set
  // the most. Category, repository and free text are applied here so they feel
  // instant.
  const state = useAsync(
    () => api.codeFindings(config, { severity: severity || undefined, status }),
    [config, severity, status],
  );

  // Only needed to offer a restore, so loaded alongside rather than blocking.
  const dismissals = useAsync(() => api.dismissals(config), [config]);

  const dismissalByFinding = useMemo(() => {
    const map: Record<string, string> = {};
    for (const dismissal of dismissals.data?.dismissals ?? []) {
      if (dismissal.findingArn) map[dismissal.findingArn] = dismissal.filterArn;
    }
    return map;
  }, [dismissals.data]);

  // Taken from the findings themselves rather than the repository list, so the
  // options are exactly the repositories that have something to report — and the
  // values match what the findings carry, including Inspector's `:branch` suffix.
  const repositories = useMemo(
    () => [...new Set((state.data?.findings ?? []).map((finding) => finding.repository))].sort(),
    [state.data],
  );

  const visible = useMemo(() => {
    const findings = state.data?.findings ?? [];
    const needle = search.trim().toLowerCase();

    return findings.filter((finding) => {
      if (category && finding.category !== category) return false;
      if (repository && finding.repository !== repository) return false;
      if (!needle) return true;

      return (
        finding.title.toLowerCase().includes(needle) ||
        finding.repository.toLowerCase().includes(needle) ||
        (finding.filePath ?? '').toLowerCase().includes(needle) ||
        (finding.vulnerabilityId ?? '').toLowerCase().includes(needle)
      );
    });
  }, [state.data, category, repository, search]);

  const counts = useMemo(
    () =>
      SEVERITIES.map((value) => ({ severity: value, count: visible.filter((f) => f.severity === value).length })).filter(
        (entry) => entry.count > 0,
      ),
    [visible],
  );

  // Repository names arrive as `org/repo:branch`, which is not a filename.
  const slug = repository ? repository.replace(/^.*\//, '').replace(/:/g, '-') : 'all-repositories';
  const today = new Date().toISOString().slice(0, 10);

  const [snippets, setSnippets] = useState<Record<string, CodeSnippet>>({});
  const [preparing, setPreparing] = useState<'pdf' | 'prompt' | 'detail'>();
  const [snippetError, setSnippetError] = useState<string>();
  const [expanded, setExpanded] = useState<string>();

  /**
   * Loads the offending code for findings that do not have it yet.
   *
   * Snippets are fetched on demand rather than with the findings, because a list view
   * shows fifty rows of code nobody has asked to see and each ten of them costs a
   * round trip to Inspector. Returns what it has either way: a snippet that cannot be
   * read is not worth failing an export over.
   */
  async function ensureSnippets(wanted: CodeFinding[], reason: 'pdf' | 'prompt' | 'detail') {
    const missing = wanted.filter((finding) => finding.filePath && !snippets[finding.id]).map((finding) => finding.id);

    if (missing.length === 0) return snippets;

    setPreparing(reason);
    setSnippetError(undefined);

    try {
      const response = await api.codeSnippets(config, missing.slice(0, 300));
      const next = { ...snippets };
      for (const snippet of response.snippets) next[snippet.findingArn] = snippet;

      setSnippets(next);
      return next;
    } catch (error) {
      setSnippetError(error instanceof Error ? error.message : 'Could not load the code for these findings.');
      return snippets;
    } finally {
      setPreparing(undefined);
    }
  }

  async function saveAsPdf() {
    await ensureSnippets(visible, 'pdf');

    // Lets React commit the snippet rows before the browser snapshots the page.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    window.print();
  }

  async function downloadPrompt() {
    const loaded = await ensureSnippets(visible, 'prompt');
    downloadText(
      buildFixPrompt(visible, loaded, repository || 'all repositories'),
      `fix-${slug}-${today}.md`,
      'text/markdown',
    );
  }

  const [dismissing, setDismissing] = useState<string>();
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string>();

  async function dismiss(finding: CodeFinding) {
    setActionError(undefined);
    setDismissing(finding.id);

    try {
      await api.dismissFinding(config, finding.id, reason.trim());
      setReason('');
      setExpanded(undefined);
      // Inspector takes up to a minute to apply the filter, so the list is reloaded
      // rather than the row being hidden optimistically — otherwise a refresh would
      // make the finding reappear and look like the dismissal failed.
      dismissals.reload();
      state.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not dismiss this finding.');
    } finally {
      setDismissing(undefined);
    }
  }

  async function restore(finding: CodeFinding) {
    const filterArn = dismissalByFinding[finding.id];
    if (!filterArn) return;

    setActionError(undefined);
    setDismissing(finding.id);

    try {
      await api.restoreFinding(config, filterArn);
      dismissals.reload();
      state.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not restore this finding.');
    } finally {
      setDismissing(undefined);
    }
  }

  async function toggleExpanded(finding: CodeFinding) {
    if (expanded === finding.id) {
      setExpanded(undefined);
      return;
    }

    setExpanded(finding.id);
    await ensureSnippets([finding], 'detail');
  }

  return (
    <>
      <div className="toolbar">
        <select value={severity} onChange={(event) => setSeverity(event.target.value)} aria-label="Severity">
          <option value="">All severities</option>
          {SEVERITIES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Scan type">
          <option value="">All scan types</option>
          {CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {CATEGORY_LABEL[value]}
            </option>
          ))}
        </select>

        <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Finding status">
          <option value="ACTIVE">Active</option>
          <option value="SUPPRESSED">Dismissed</option>
        </select>

        <select value={repository} onChange={(event) => setRepository(event.target.value)} aria-label="Repository">
          <option value="">All repositories</option>
          {repositories.map((value) => (
            <option key={value} value={value}>
              {value.replace(/^[^/]+\//, '')}
            </option>
          ))}
        </select>

        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Filter by repository, file or CVE"
          aria-label="Filter findings"
          style={{ minWidth: 280 }}
        />

        <button className="action secondary" onClick={state.reload}>
          Refresh
        </button>

        <div className="toolbar-end">
          <button
            className="action secondary"
            disabled={visible.length === 0}
            onClick={() => downloadCsv(visible, `code-findings-${slug}-${today}.csv`)}
          >
            Download CSV
          </button>

          {/* Deliberately the browser's own print-to-PDF rather than generating a
              PDF server side, which would mean bundling a renderer into the API
              Lambda to produce a document the browser already makes. */}
          <button
            className="action secondary"
            disabled={visible.length === 0 || preparing !== undefined}
            onClick={saveAsPdf}
          >
            {preparing === 'pdf' ? 'Preparing report…' : 'Save as PDF'}
          </button>

          {/* A findings list is only useful if someone acts on it, and the fixes are
              mostly mechanical, so the report can also leave as instructions. */}
          <button
            className="action secondary"
            disabled={visible.length === 0 || preparing !== undefined}
            onClick={downloadPrompt}
            title="Markdown file describing every finding and the AWS-recommended fix, to attach to a Cursor chat"
          >
            {preparing === 'prompt' ? 'Preparing prompt…' : 'Cursor fix prompt'}
          </button>
        </div>
      </div>

      {snippetError ? (
        <div className="no-print">
          <Notice kind="warn">
            {snippetError} The report can still be printed, but without the code for some findings.
          </Notice>
        </div>
      ) : null}

      {actionError ? (
        <div className="no-print">
          <Notice kind="error">{actionError}</Notice>
        </div>
      ) : null}

      {status === 'SUPPRESSED' ? (
        <div className="no-print">
          <Notice>
            Dismissed findings are hidden from the Active list and from exported reports, but nothing is deleted.
            Restoring one brings it back within about a minute.
          </Notice>
        </div>
      ) : null}

      <AsyncBoundary state={state}>
        {() =>
          visible.length === 0 ? (
            <Notice>No findings match these filters.</Notice>
          ) : (
            <div className="card">
              {/* Only rendered on paper. A report that leaves the portal has to say
                  what it covers and when it was taken, or the reader cannot tell a
                  stale export from a current one. */}
              <div className="print-header">
                <h1>Code security findings</h1>
                <dl>
                  <dt>Repository</dt>
                  <dd>{repository || 'All repositories'}</dd>
                  <dt>Scope</dt>
                  <dd>
                    {severity || 'All severities'}, {category ? CATEGORY_LABEL[category as ScanCategory] : 'all scan types'}
                    {search.trim() ? `, matching "${search.trim()}"` : ''}
                  </dd>
                  <dt>Generated</dt>
                  <dd>{new Date().toLocaleString()}</dd>
                  <dt>Source</dt>
                  <dd>Amazon Inspector code security, via Lighthouse ({config.environment})</dd>
                </dl>
                <p>
                  {visible.length} active finding{visible.length === 1 ? '' : 's'}
                  {counts.length > 0 ? `: ${counts.map((c) => `${c.count} ${c.severity.toLowerCase()}`).join(', ')}` : ''}.
                  Findings come from scans of the repository's default branch; issues raised only as pull request review
                  comments are not included.
                </p>
              </div>

              <p className="muted no-print" style={{ marginTop: 0 }}>
                {visible.length} finding{visible.length === 1 ? '' : 's'}
              </p>
              <table>
                <thead>
                  <tr>
                    <th>Severity</th>
                    <th>Finding</th>
                    <th>Repository</th>
                    <th>Location</th>
                    <th>Type</th>
                    <th>First seen</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.flatMap((finding) => [
                    <tr
                      key={finding.id}
                      className={`finding-row${expanded === finding.id ? ' expanded' : ''}`}
                      onClick={() => void toggleExpanded(finding)}
                      aria-expanded={expanded === finding.id}
                    >
                      <td>
                        <SeverityBadge value={finding.severity} />
                      </td>
                      <td>
                        <div className="finding-title">{finding.title}</div>
                        <div className="finding-desc">{finding.description}</div>
                        {finding.cwes.length > 0 ? <code>{finding.cwes.join(', ')}</code> : null}
                      </td>
                      {/* The org prefix is the same for every repository we scan, so
                          it only costs width — three wrapped lines per row in print. */}
                      <td>{finding.repository.replace(/^[^/]+\//, '')}</td>
                      <td>
                        {finding.filePath ? (
                          <code>
                            {finding.filePath}
                            {finding.startLine ? `:${finding.startLine}` : ''}
                          </code>
                        ) : (
                          <span className="muted">—</span>
                        )}
                        {finding.vulnerabilityId ? <div><code>{finding.vulnerabilityId}</code></div> : null}
                      </td>
                      <td>
                        <span className="badge info">{finding.category}</span>
                        {finding.fixAvailable === 'YES' ? (
                          <div>
                            <span className="badge ok">FIX AVAILABLE</span>
                          </div>
                        ) : null}
                      </td>
                      <td className="muted">{formatDate(finding.firstObservedAt)}</td>
                    </tr>,
                    // A second row, on paper only, holding the code and the fix. Kept
                    // out of the screen table because it would bury the list, and out
                    // of the first row so the columns still line up.
                    <tr
                      key={`${finding.id}-detail`}
                      className={`print-detail${expanded === finding.id ? ' open' : ''}`}
                    >
                      <td />
                      <td colSpan={5}>
                        {finding.fullFilePath ? <div className="detail-path">{finding.fullFilePath}</div> : null}

                        {/* Only shown on screen: the metadata that does not earn a
                            column in the table but is the first thing you want when
                            looking at one finding. */}
                        <dl className="detail-meta no-print">
                          {finding.detectorName ? (
                            <>
                              <dt>Detector</dt>
                              <dd>{finding.detectorName}</dd>
                            </>
                          ) : null}
                          {finding.ruleId ? (
                            <>
                              <dt>Rule</dt>
                              <dd>
                                <code>{finding.ruleId}</code>
                              </dd>
                            </>
                          ) : null}
                          <dt>Status</dt>
                          <dd>{finding.status}</dd>
                          <dt>Last seen</dt>
                          <dd>{formatDate(finding.lastObservedAt)}</dd>
                        </dl>

                        {preparing === 'detail' && !snippets[finding.id] ? (
                          <p className="muted no-print">Loading the code…</p>
                        ) : null}

                        <p className="detail-desc no-print">{finding.description}</p>

                        {expanded === finding.id && finding.cwes.length > 0 ? (
                          <p className="no-print">
                            {finding.cwes.map((cwe) => (
                              <a
                                key={cwe}
                                href={`https://cwe.mitre.org/data/definitions/${cwe.replace(/\D/g, '')}.html`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(event) => event.stopPropagation()}
                              >
                                {cwe}{' '}
                              </a>
                            ))}
                          </p>
                        ) : null}

                        {snippets[finding.id]?.lines.length ? (
                          <pre className="snippet">
                            {snippets[finding.id].lines.map((line) => {
                              const start = finding.startLine ?? snippets[finding.id].startLine;
                              const end = finding.endLine ?? start;
                              const flagged = start !== undefined && line.lineNumber >= start && line.lineNumber <= (end ?? start);

                              return (
                                <div key={line.lineNumber} className={flagged ? 'line flagged' : 'line'}>
                                  <span className="ln">{line.lineNumber}</span>
                                  <span className="code">{line.content}</span>
                                </div>
                              );
                            })}
                          </pre>
                        ) : null}

                        {finding.remediationText ? (
                          <div className="remediation">
                            <strong>Suggested fix: </strong>
                            {plainText(finding.remediationText)}
                            {finding.remediationUrl ? (
                              <>
                                {' '}
                                <a
                                  className="no-print"
                                  href={finding.remediationUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  Detector guidance
                                </a>
                              </>
                            ) : null}
                          </div>
                        ) : null}

                        {/* Dismissal lives next to the evidence, because deciding a
                            finding is wrong should mean having read the code. */}
                        {expanded === finding.id && isAdmin ? (
                          <div className="dismiss-row no-print" onClick={(event) => event.stopPropagation()}>
                            {status === 'SUPPRESSED' ? (
                              <>
                                <span className="muted">
                                  Dismissed
                                  {dismissals.data?.dismissals.find((d) => d.findingArn === finding.id)?.reason
                                    ? `: ${dismissals.data.dismissals.find((d) => d.findingArn === finding.id)?.reason}`
                                    : ''}
                                </span>
                                <button
                                  className="action secondary"
                                  disabled={dismissing === finding.id || !dismissalByFinding[finding.id]}
                                  onClick={() => void restore(finding)}
                                >
                                  {dismissing === finding.id ? 'Restoring…' : 'Restore'}
                                </button>
                              </>
                            ) : (
                              <>
                                <input
                                  value={reason}
                                  onChange={(event) => setReason(event.target.value)}
                                  placeholder="Why is this not a real problem?"
                                  aria-label="Reason for dismissing"
                                  style={{ flex: 1, minWidth: 240 }}
                                />
                                <button
                                  className="action secondary"
                                  disabled={dismissing === finding.id || reason.trim().length < 10}
                                  onClick={() => void dismiss(finding)}
                                >
                                  {dismissing === finding.id ? 'Dismissing…' : 'Dismiss as false positive'}
                                </button>
                              </>
                            )}
                          </div>
                        ) : null}

                        {snippets[finding.id]?.suggestedFixes.map((fix, index) => (
                          <div key={index} className="remediation">
                            {fix.description ? <div>{fix.description}</div> : null}
                            {fix.code ? <pre className="snippet patch">{fix.code}</pre> : null}
                          </div>
                        ))}
                      </td>
                    </tr>,
                  ])}
                </tbody>
              </table>
            </div>
          )
        }
      </AsyncBoundary>
    </>
  );
}
